// Spaces on a phone, after SPhoneSpaces: one row per space (its mark, the client, tracks, open and activities),
// then what you opened recently on this phone.
import { AddButton } from '../ui/AddButton';
import { useState } from 'react';
import { shownSpaceColor } from '@dpw/shared';
import { AnimatePresence } from 'motion/react';
import { useMe, useSpaces } from '../lib/queries';
import { SpaceSheet, createsSpaces } from '../ui/SpaceForm';
import { ErrorNote } from '../ui/bits';
import { SkelList } from '../ui/motion';
import { BellButton, P, Row, SearchButton, Section, Title, TopBar } from './kit';
import { readRecent } from './recent';
import { useNewSince } from '../lib/seen';
import { NewDot } from '../ui/NewDot';

/** A space's mark: a short capitalised first word as it is ("AI"), else the first letters of two words ("HV"). */
const mark = (name: string) => {
  const words = name.split(/\s+/).filter(w => /^[A-Za-z]/.test(w));
  if (words[0] && /^[A-Z]{2,3}$/.test(words[0])) return words[0];
  return words.slice(0, 2).map(w => w[0]!.toUpperCase()).join('');
};
const n = (k: number, one: string, many: string) => <span><b>{k}</b> {k === 1 ? one : many}</span>;

export function PhoneSpaces() {
  const q = useSpaces();
  const me = useMe().data;
  const [creating, setCreating] = useState(false);
  const recent = readRecent();
  const dots = useNewSince();
  return (
    <div className="ph-page">
      <TopBar><SearchButton /><BellButton /></TopBar>
      <Title title="Spaces" sub="One space per client or internal programme" />
      {q.isLoading ? <SkelList rows={3} variant="conv" /> : q.error ? <ErrorNote error={q.error} /> : (
        <div className="ph-grp" role="group" aria-label="Spaces">
          {(q.data ?? []).map(s => (
            <Row key={s.id} to={`/spaces/${s.id}`} className="big" label={s.name}
              icon={<span className="ph-sq" style={{ background: `var(--${shownSpaceColor(s.color)})` }} aria-hidden="true">{mark(s.name)}</span>} iconTone="bare"
              title={<>{s.name}{dots.space(s.id) && <NewDot />}</>} sub={<>{s.clientName ?? 'Internal'}<span className="ph-mini3">{n(s.tracks, 'track', 'tracks')}{n(s.open, 'open', 'open')}{n(s.activities, 'activity', 'activities')}</span></>} />
          ))}
        </div>
      )}
      {q.data && !q.data.length && (
        <div className="ph-empty3" role="group" aria-label="No spaces yet">
          <b>No spaces yet</b>
          <span>{me && createsSpaces(me) ? 'Create the first one: a client engagement or an internal programme.' : 'The director adds you to a space; it shows here.'}</span>
          {me && createsSpaces(me) && <AddButton className="wide" onClick={() => setCreating(true)}>New space</AddButton>}
        </div>
      )}
      <AnimatePresence>{creating && me && <SpaceSheet key="new-space" me={me} onClose={() => setCreating(false)} />}</AnimatePresence>
      {recent.length > 0 && <>
        <Section title="Recently opened" />
        <div className="ph-grp" role="group" aria-label="Recently opened">
          {recent.map(r => <Row key={r.to} to={r.to} icon={r.kind === 'activity' ? <P.doc /> : <P.list />} iconTone={r.kind === 'activity' ? 'blu' : ''} title={r.title} sub={r.sub} />)}
        </div>
      </>}
    </div>
  );
}
